import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Select, SelectTrigger } from "@activecollab/components";
import moment from "moment";
import PropTypes from "prop-types";
import { getClosestOccurrence, isAllDay } from "~/components/Calendar/calendarUtils";
import { getUserTimezone } from "~/store/selectors/selector.configOptions";
import { RELATIVE_REMINDERS_SUPPORTED_PARENT_TYPES, resolveRelativeValues } from "../utils";

export const RelativeReminderOffset = ({
  parent,
  relatedField,
  relativeValue,
  relativeUnit,
  relativeValueModifier,
  showErrors,
  onChange,
}) => {
  const { t } = useTranslation();
  const timezone = useSelector(getUserTimezone);
  const amountOptions = useMemo(() => {
    if (parent.class === "Task") {
      return resolveRelativeValues(moment.unix(parent[relatedField]), relativeUnit, relativeValueModifier);
    }
    if (parent.class === "CalendarEvent") {
      return resolveRelativeValues(moment(getClosestOccurrence(parent, timezone)), relativeUnit, relativeValueModifier);
    }
    return [];
  }, [parent, relatedField, relativeUnit, relativeValueModifier, timezone]);

  const unitOptions = useMemo(() => {
    if (RELATIVE_REMINDERS_SUPPORTED_PARENT_TYPES.includes(parent.class)) {
      let result = [
        {
          id: 1,
          name: t("days before"),
          unit: "day",
          modifier: -1,
          defultValue: 1,
        },
        {
          id: 2,
          name: t("days after"),
          unit: "day",
          modifier: 1,
          defultValue: 1,
        },
      ];

      if (parent.class === "CalendarEvent" && !isAllDay(parent)) {
        return [
          ...result,
          ...[
            {
              id: 3,
              name: t("hour before"),
              unit: "hour",
              modifier: -1,
              defultValue: 1,
            },
            {
              id: 4,
              name: t("hour after"),
              unit: "hour",
              modifier: 1,
              defultValue: 1,
            },
            {
              id: 5,
              name: t("minutes before"),
              unit: "minute",
              modifier: -1,
              defultValue: 15,
            },
            {
              id: 6,
              name: t("minutes after"),
              unit: "minute",
              modifier: 1,
              defultValue: 15,
            },
          ],
        ];
      }

      return result;
    }

    return [];
  }, [parent, t]);

  const selectedUnit = useMemo(() => {
    if (unitOptions.length) {
      return unitOptions.find((option) => option.unit === relativeUnit && option.modifier === relativeValueModifier);
    }
    return null;
  }, [relativeUnit, relativeValueModifier, unitOptions]);

  const handleChange = useCallback(
    (values) => {
      let isTodaySelected = false;
      if (parent.class === "CalendarEvent") {
        isTodaySelected =
          moment().format("YYYY-MM-DD") ===
          moment(getClosestOccurrence(parent, timezone))
            .utc()
            .add(values.relative_value * values.relative_value_modifier, values.relative_unit)
            .format("YYYY-MM-DD");
      } else {
        isTodaySelected =
          moment().format("YYYY-MM-DD") ===
          moment
            .unix(parent[relatedField])
            .utc()
            .add(values.relative_value * values.relative_value_modifier, values.relative_unit)
            .format("YYYY-MM-DD");
      }

      if (typeof onChange === "function") {
        onChange({ ...values, is_today_selected: isTodaySelected });
      }
    },
    [onChange, parent, relatedField, timezone]
  );

  return (
    <div className="tw-flex tw-gap-2 tw-items-center tw-flex-col sm:tw-flex-row tw-w-full sm:tw-w-fit">
      <Select
        options={amountOptions}
        onChange={(value) => {
          handleChange({
            relative_value: value,
            relative_value_modifier: relativeValueModifier,
            relative_unit: relativeUnit,
          });
        }}
        target={
          <SelectTrigger
            className="tw-w-full sm:tw-w-[84px]"
            invalid={showErrors && amountOptions.filter((o) => o.id === relativeValue).length === 0}
            disabled={amountOptions.length === 0}
          >
            {relativeValue}
          </SelectTrigger>
        }
        type="single"
        keepSameOptionsOrder
        disabledInternalSort
        disableSearch
        selected={[relativeValue]}
        renderOption={(option) => option.name}
        forceCloseMenu
      />

      <Select
        options={unitOptions}
        onChange={(optionId) => {
          const selectedOption = unitOptions.find((option) => option.id === optionId);
          handleChange({
            relative_value: selectedOption.unit !== relativeUnit ? selectedOption.defultValue : relativeValue,
            relative_value_modifier: selectedOption.modifier,
            relative_unit: selectedOption.unit,
          });
        }}
        target={
          <SelectTrigger className="tw-w-full sm:tw-w-[154px]">{selectedUnit ? selectedUnit.name : null}</SelectTrigger>
        }
        keepSameOptionsOrder
        disabledInternalSort
        type="single"
        disableSearch
        selected={[selectedUnit ? selectedUnit.id : null]}
        renderOption={(option) => option.name}
        forceCloseMenu
      />
    </div>
  );
};

RelativeReminderOffset.propTypes = {
  parent: PropTypes.object,
  relatedField: PropTypes.string,
  relativeValue: PropTypes.number,
  relativeUnit: PropTypes.string,
  relativeValueModifier: PropTypes.number,
  showErrors: PropTypes.bool,
  onChange: PropTypes.func,
};
